import { CreateCryptoPaymentRequest } from 'proto/billing-manager_pb';

// Translate a resolved checkout plan (from resolveFullCheckoutUpdates) into a
// CreateCryptoPaymentRequest payload. The crypto cron splits multi-item
// CreateSubscription payloads into one Stripe sub per item, so new items —
// both fresh-sub creates AND adds onto existing subs — get funneled through
// the same items[] list. Modify ops (qty change on an existing sub) keep
// their dedicated ModifySubscription path; only one is supported per call.

const PLAN_TYPE_MULTI_CYCLE = 'multi-cycle';

export type CryptoLineItem = { priceId: string; quantity: number };

type PlanCreateItem = { price_id: string; quantity: number };
type PlanModifyItem = { subscriptionId: string; quantity: number; subscriptionItemId?: string; priceId?: string };
type PlanOp = {
  creates?: PlanCreateItem[];
  adds?: PlanCreateItem[];
  modifies?: PlanModifyItem[];
  isDedi?: boolean;
};
type Plan = { type?: string; ops?: PlanOp[] } | null | undefined;

export type CryptoTranslationInput = {
  userId: string;
  plan: Plan;
  purchaseTotalCents: number;
  creditPurchaseTotalCents?: number;
  useTrial?: boolean;
  toltReferral?: string;
  coupon?: string;
};

export function planToCryptoRequest({
  userId,
  plan,
  purchaseTotalCents,
  creditPurchaseTotalCents = 0,
  useTrial = false,
  toltReferral = '',
  coupon = ''
}: CryptoTranslationInput): CreateCryptoPaymentRequest.AsObject | null {
  if (!plan || plan.type !== PLAN_TYPE_MULTI_CYCLE) return null;

  const newSubItems: PlanCreateItem[] = [];
  let isDedi = false;
  let firstModify: PlanModifyItem | null = null;

  for (const op of plan.ops || []) {
    if (op.creates?.length) {
      newSubItems.push(...op.creates);
      if (op.isDedi) isDedi = true;
    }
    if (op.adds?.length) {
      // Adds onto existing subs are routed as new parallel subs for crypto:
      // each item ends up as its own Stripe subscription with
      // cancel_at_period_end so the missing recurring rail is harmless.
      newSubItems.push(...op.adds);
      if (op.isDedi) isDedi = true;
    }
    if (!firstModify && op.modifies?.length) {
      firstModify = op.modifies[0];
    }
  }

  const purchaseTotal = purchaseTotalCents / 100;
  const creditPurchaseTotal = creditPurchaseTotalCents / 100;

  if (newSubItems.length > 0) {
    const [first] = newSubItems;
    return {
      userId,
      createSubscription: {
        userId,
        priceId: first.price_id,
        quantity: first.quantity,
        toltReferral,
        coupon,
        isDedi,
        useTrial,
        itemsList: newSubItems.map((c) => ({ priceId: c.price_id, quantity: c.quantity }))
      } as any,
      modifySubscription: undefined as any,
      purchaseTotal,
      creditPurchaseTotal,
      payInvoiceId: ''
    };
  }

  if (firstModify) {
    return {
      userId,
      createSubscription: undefined as any,
      modifySubscription: {
        userId,
        subscriptionId: firstModify.subscriptionId,
        quantity: firstModify.quantity,
        subscriptionItemId: firstModify.subscriptionItemId || '',
        // Carry the swap target SKU so the crypto cron can do an in-place price
        // change; empty when it's a plain quantity modify.
        priceId: firstModify.priceId || ''
      } as any,
      purchaseTotal,
      creditPurchaseTotal,
      payInvoiceId: ''
    };
  }

  return null;
}

// collectCryptoLineItems flattens every create + add item from a plan into a
// single list, mirroring what planToCryptoRequest sends to the backend.
// CryptoElement uses it to render an itemized pre-submit preview without
// rebuilding the routing rules here.
export function collectCryptoLineItems(plan: Plan): CryptoLineItem[] {
  if (!plan || plan.type !== PLAN_TYPE_MULTI_CYCLE) return [];
  const items: CryptoLineItem[] = [];
  for (const op of plan.ops || []) {
    for (const c of op.creates || []) items.push({ priceId: c.price_id, quantity: c.quantity });
    for (const a of op.adds || []) items.push({ priceId: a.price_id, quantity: a.quantity });
  }
  return items;
}
